/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useUser } from "./UserContext";
import { Link, useLocation } from "react-router-dom";
import clsx from "clsx";
import iconMap from "src/assets/icons/Icons";
import {
  FontColor,
  ThemeBasedColorHeader,
  ThemeBasedColorMenubar,
} from "src/shared/modules/profile/theme/themeCheacker";
import usePageSize from "../hooks/pageSize";
import usePermission from "../hooks/usePermission";
import { reactAppVersion } from "src/features/auth/axiosConfig";

type Props = {
  menubar: () => void;
};

const Menu: React.FC<Props> = ({ menubar }) => {
  const { user } = useUser();
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1000);
  const themeColor = user?.themes.backgroundColour || "Blue";

  // ✅ Throttled resize handler (to prevent continuous re-renders)
  useEffect(() => {
    const handleResize = () => {
      window.requestAnimationFrame(() => {
        setIsMobile(window.innerWidth < 1000);
      });
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  const view = usePageSize();
  const { hasPermission } = usePermission();

  /* ✅ Menu Config */
  const menuItems = useMemo(
    () => [
      { id: "home", path: `/grocery/${reactAppVersion}/home`, name: "Home", icon: iconMap["home"] },

      {
        id: "dashboard",
        path: `/grocery/${reactAppVersion}/dashboard`,
        name: "Dashboard",
        icon: iconMap["Dashboard"],
        permission: "dashboard_view",
      },
      {
        id: "orders",
        path: `/grocery/${reactAppVersion}/orders`,
        name: "Orders",
        icon: iconMap["products"],
        permission: "order_view",
      },
      {
        id: "order_return",
        path: `/grocery/${reactAppVersion}/order_return`,
        name: "Return Order",
        icon: iconMap["ReturnOrder"],
        permission: "order_cancel",
      },
      {
        id: "ledgers",
        path: `/grocery/${reactAppVersion}/ledgers`,
        name: "Ledgers",
        icon: iconMap["wallet"],
        permission: "customer_view",
      },
      {
        id: "settings",
        path: `/grocery/${reactAppVersion}/admin`,
        name: "Admin",
        icon: iconMap["settings"],
        permission: "management_view",
      },
    ],
    []
  );

  /* ✅ Render Icon */
  const renderIcon = useCallback(
    (
      IconComponent: React.ElementType,
      path: string,
      name: string,
      id: string,
      permission?: string
    ) => {
      const isActive = location.pathname.startsWith(path);

      const allow = hasPermission(permission); // ✅ handles undefined also

      if (!allow) return null; // 🔥 important

      return (
        <Link
          key={id}
          id={id}
          to={path}
          className={clsx(
            "flex w-full items-center whitespace-nowrap font-bold py-1 my-4 transition-all duration-200",
            {
              "border border-gray-200 rounded-md shadow-lg": isActive,
              [ThemeBasedColorHeader()]: isActive,
              "hover:-translate-y-1.5": !isActive,
              [FontColor()]: !isActive,
            }
          )}
          aria-label={view !== "pos" ? name : undefined}
          title={view !== "pos" ? name : undefined}
        >
          <IconComponent />
          {view === "pos" && (
            <span className="ml-2 truncate" title={name}>
              {name}
            </span>
          )}
        </Link>
      );
    },
    [location.pathname, view, hasPermission]
  );

  return (
    <div
      className={clsx(
        "w-fit h-full overflow-hidden rounded-r-2xl flex flex-col items-center pt-4",
        ThemeBasedColorMenubar()
      )}
    >
      <div className="w-full p-2">
        {menuItems.map(({ id, path, name, icon, permission }) =>
          renderIcon(icon, path, name, id, permission)
        )}
      </div>
    </div>
  );
};

export default React.memo(Menu);